import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useInViewOnce } from "@/hooks/useInViewOnce";

interface SectionHeadingProps {
  eyebrow?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  align?: "center" | "left";
  /** "dark" recolors the eyebrow/title/subtitle for use over a dark immersive band. */
  tone?: "light" | "dark";
  className?: string;
}

/** Eyebrow + title + subtitle with a one-shot scroll reveal. Brand colors only. */
const SectionHeading = ({
  eyebrow,
  title,
  subtitle,
  align = "center",
  tone = "light",
  className,
}: SectionHeadingProps) => {
  const { ref, inView } = useInViewOnce<HTMLDivElement>();
  const dark = tone === "dark";

  return (
    <div
      ref={ref}
      className={cn(
        "max-w-3xl transition-all duration-700 ease-out",
        align === "center" ? "mx-auto text-center" : "text-left",
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
        className
      )}
    >
      {eyebrow && (
        <div className={cn("mb-3", align === "center" && "flex justify-center")}>
          <span
            className={cn(
              "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide text-genie-primary",
              dark ? "bg-white/10 ring-1 ring-white/10" : "bg-genie-primary/10"
            )}
          >
            {eyebrow}
          </span>
        </div>
      )}
      <h2
        className={cn(
          "text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight",
          dark ? "text-white" : "text-[#222B45]"
        )}
      >
        {title}
      </h2>
      {subtitle && (
        <p
          className={cn(
            "mt-4 text-base sm:text-lg leading-relaxed",
            dark ? "text-white/70" : "text-[#64748B]"
          )}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
};

export default SectionHeading;
